import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IPlainObject } from '../../models';
import { is } from '../is';
import { removeEmptyValues } from '../removeEmptyValues';

export interface UseLocationQueryResult<T> {
  query: T;
  pathname: string;
  setSearchParam: (params: IPlainObject) => void;
}

function stringifyValuesToJson(obj: IPlainObject): IPlainObject {
  return Object.keys(obj).reduce(
    (acum, key) => ({ ...acum, [key]: JSON.stringify(obj[key]) }),
    {},
  );
}

function parseJsonToValues<T = IPlainObject>(
  urlSearchParams: URLSearchParams,
): T {
  const result = {} as T;

  urlSearchParams.forEach((value, key) => {
    try {
      result[key] = JSON.parse(value);
    } catch (e) {
      result[key] = value;
    }
  });

  return result;
}

function stringifyLocation(params: IPlainObject): string {
  return new URLSearchParams(
    stringifyValuesToJson(removeEmptyValues({ ...params })),
  ).toString();
}

export function parseLocation<T = IPlainObject>(source?: string): T {
  const { search } = location;

  // return (source || search || '')
  //   .replace(/\??/gi, '')
  //   .split('&')
  //   .reduce<T>((acum, item) => {
  //     if (!item) {
  //       return acum;
  //     }
  //
  //     const [key, value] = item.split('=');
  //
  //     return {
  //       ...acum,
  //       [key]: decodeURIComponent(value),
  //     };
  //   }, {} as T);

  const urlEntity = new URLSearchParams(source || search || '');

  return parseJsonToValues<T>(urlEntity);
}

export function useLocationQuery<T = IPlainObject>(): UseLocationQueryResult<
  Partial<T>
> {
  const { search, pathname } = useLocation();
  const navigate = useNavigate();

  const query: Partial<T> = React.useMemo(
    () => parseLocation<T>(search),
    [search],
  );

  const setSearchParam = (
    params: IPlainObject,
    options: { replace?: boolean } = {},
  ) => {
    const queryString = stringifyLocation({ ...query, ...params });

    if (search === queryString) {
      return;
    }

    navigate(`${pathname}?${queryString}`, { replace: true, ...options });
  };

  return {
    pathname,
    query,
    setSearchParam,
  };
}

export function useLocationState<T = string | string[]>(
  key: string,
  defaultState?: T,
): [s: T, st: (_s: T, skipURL?: boolean) => void] {
  const { query, setSearchParam } = useLocationQuery();

  const urlValue = useMemo(
    () => (is.nullOrUndefined(query && query[key]) ? defaultState : query[key]),
    [query && query[key]],
  );
  const [state, setState] = useState<T>(urlValue);

  const set = (newValue: T, skipURL?: boolean) => {
    if (!skipURL) {
      setSearchParam({ [key]: newValue });
    }

    setState(newValue);
  };

  return [state, set];
}
